/**
 * The free market — shapes and numbers, no behaviour.
 *
 * The owner's brief, in the owner's words: *"markets should have a distance
 * limit which can be increased with navy size and you should put resources on
 * sale, same way you should be able to buy from others, you can select the
 * price you want, and so should ai, to make a free market, and you buy the
 * cheapest."*
 *
 * So: an order book. Any crown may post goods at a price of its choosing, any
 * crown may bid at a price of its choosing, and once a month — at the council,
 * deterministically, with no random number anywhere in it — the book is
 * matched: cheapest ask against highest bid, trades executing at the **sell**
 * price, which is what "you buy the cheapest" means. Distance gates who may
 * trade with whom, and hulls extend it. The verbs live in `market.ts`, the AI
 * in `marketai.ts`; this file imports nothing but the resource vocabulary, so
 * everything else may import it freely.
 */

import { RESOURCE_KEYS, type ResourceKey } from '../types.ts';

/** Which side of the book an order sits on. */
export const OrderKind = {
  /** Goods on the table, escrowed, waiting for a buyer. */
  Sell: 0,
  /** A standing bid: gold promised, paid only when a match lands. */
  Buy: 1,
} as const;
export type OrderKindId = (typeof OrderKind)[keyof typeof OrderKind];

/**
 * One open order on the realm's book.
 *
 * ── Escrow, and which side carries it ─────────────────────────────────────
 *
 * **A sell order escrows its goods.** The amount leaves the seller's treasury
 * the moment the order is posted and comes back only on cancel or expiry — so a
 * crown cannot promise the same forty iron to the market and to next month's
 * muster, and matching can never fail on the sell side. The alternative
 * (re-validate at match time, cancel uncovered orders) was rejected because it
 * makes the book a list of *claims* rather than of goods: every reader of it —
 * the panel, the AI pricing its own asks against the competition — would be
 * reasoning about offers that might silently be lies.
 *
 * **A buy order escrows nothing.** Gold is the one resource every system spends
 * every month — wages, hulls, works — and locking a bid's worth of it away
 * would starve upkeep for an order that may never fill. So a bid is re-validated
 * at match time instead: it fills only up to what the bidder can pay at that
 * moment, and an unpayable bid simply waits. The asymmetry is deliberate and
 * this paragraph is its record.
 *
 * `price` is an integer number of **tenths of a gold per unit** — fixed-point,
 * because the book is hashed and saved and a price must never be a float that
 * rounds differently from itself. `amount` is an integer number of units and
 * stays one through every partial fill.
 */
export interface MarketOrder {
  id: number;
  /** Nation index that posted it. */
  nation: number;
  /** An `OrderKind`. */
  kind: number;
  key: ResourceKey;
  /** Units still open. Always a non-negative integer. */
  amount: number;
  /** Tenths of a gold per unit. Always a positive integer. */
  price: number;
  /** The turn it was posted. Drives expiry, and the panel prints it. */
  placedTurn: number;
}

/** The book. Lives on `CampaignState.market`; hashed and saved in full. */
export interface MarketState {
  /** Open orders, in posting order. Matching sorts its own copies. */
  orders: MarketOrder[];
  /** Ids count from 1, so 0 is free to mean "no order". */
  nextOrderId: number;
}

export function emptyMarket(): MarketState {
  return { orders: [], nextOrderId: 1 };
}

// ── Reach ──────────────────────────────────────────────────────────────────

/**
 * Hops on the combined road+lane graph a crown's merchants cover unaided.
 *
 * Six, measured against the board rather than guessed: capital-to-capital
 * distances on the current realm run 1 to 17 hops on the combined graph, and
 * six covers about a third of all pairs — a crown's own region and the
 * neighbours one sea over, which is the neighbourhood it can already march or
 * sail to in a season. A base reach that covered everything would make the navy
 * term decorative; one that covered only adjacency would make the opening
 * market four crowns deep.
 */
export const BASE_REACH_HOPS = 6;

/**
 * Hulls per extra hop of reach.
 *
 * Three, set against the fleet caps: a crown at `MAX_FLEETS_PER_NATION` ×
 * `MAX_FLEET_SHIPS` floats 42 hulls, which is +14 hops — reach 20, against a
 * measured worst pair of 17. So a full navy can trade with anybody from
 * anywhere, a single minimum squadron (2 hulls) buys nothing, and every three
 * hulls after that visibly widens the map. Two crowns may trade if **either**
 * can reach the other: a big navy carries the goods both ways.
 */
export const HULLS_PER_REACH = 3;

// ── The book's hygiene ─────────────────────────────────────────────────────

/**
 * Live orders one crown may have on the book at once.
 *
 * Six, so the book stays readable: forty-two crowns at six orders is a worst
 * case of ~250 lines, and in practice a tenth of that. It is also the AI's
 * budget, which keeps any one crown from papering the book.
 */
export const MAX_LIVE_ORDERS = 6;

/**
 * Months an order stands before it lapses and the escrow comes home.
 *
 * A year. Long enough that a fair ask survives a slow season; short enough
 * that the book is not an archaeology of prices nobody would pay any more.
 * Expiry is also what stops a dead crown's escrow being locked up forever —
 * elimination cancels the orders outright.
 */
export const ORDER_TTL_MONTHS = 12;

/** Price bounds, in tenths of a gold. A free price is still a sane price. */
export const MIN_PRICE_TENTHS = 1;
export const MAX_PRICE_TENTHS = 10000;

/**
 * Units one order may carry. Two orders of magnitude above the deepest
 * treasury a capped resource can hold, so no honest order ever meets it — the
 * bound exists because `amount` is hashed with `u32` and saved through JSON,
 * and an unbounded bid (`Infinity` hashes as 0, saves as `null`, loads as a
 * dead order) is a world the hash calls identical to a different one.
 */
export const MAX_ORDER_UNITS = 100_000;

// ── Prices ─────────────────────────────────────────────────────────────────

/**
 * Base worth of a unit, in tenths of a gold — the AI's anchor, not a rule. The
 * player may post any price they like; these are what the crowns price around.
 *
 * The values mirror the settlement market's `BASE_PRICES` in `apply.ts` (0.5
 * gold timber, 2.4 iron, and so on), and they are a copy rather than an import
 * on purpose: `apply.ts` imports the campaign's command router, so importing it
 * back from here would close a module cycle through half the simulation. The
 * two tables price different markets — a town's stall against a realm's bourse
 * — and may drift apart deliberately; what must not drift is the *relative*
 * order of goods, which both take from the production chain.
 *
 * A key without an entry cannot be posted at all, the same standing rule the
 * settlement market keeps: better an honest refusal than a good silently
 * priced at zero. Gold itself is deliberately absent — a market where gold
 * buys gold is a rounding-error printing press.
 */
export const MARKET_PRICES: Partial<Record<ResourceKey, number>> = {
  timber: 5,
  planks: 16,
  stone: 9,
  iron: 24,
  grain: 6,
  bread: 15,
  essence: 45,
  magewood: 80,
  runestone: 90,
  ingots: 75,
};

/** The tradeable keys, in `RESOURCE_KEYS` order so every walk is stable. */
export const MARKET_KEYS: readonly ResourceKey[] = RESOURCE_KEYS.filter(
  (k) => MARKET_PRICES[k] !== undefined,
);

// ── The AI's posture ───────────────────────────────────────────────────────
//
// The numbers the crowns post with. Sell asks start above base and settle
// toward it as competition arrives; bids start well below base and climb
// toward — and slightly past — it as the shortfall bites. The two bands
// overlap only when a seller is undercut or a buyer is desperate, which is
// what makes a fill an event rather than a subsidy.

/**
 * What a crown keeps of each good before it will sell a unit. Anything not in
 * this table the AI never sells — looted exotica sits in the treasury rather
 * than being dumped at a price nobody tuned.
 *
 * Iron's floor sits above the buy target below on purpose (keep 60, want 40):
 * a crown can never be posting both sides of the same good, so it can never
 * cross its own book.
 *
 * **Grain is the exception and is deliberately absent from both tables.** A
 * crown's food floor is its monthly bill times `FOOD_KEEP_MONTHS`, which is a
 * function of the ground it holds and the hosts it feeds rather than a constant
 * — see `keepFloor` in `marketai.ts` and the argument in `food.ts`. A flat 20
 * was what made the book a third grain asks that nobody would ever bid on.
 */
export const AI_KEEP: Partial<Record<ResourceKey, number>> = {
  timber: 45,
  stone: 30,
  iron: 60,
  bread: 0,
  planks: 0,
};

/**
 * What a crown tries to hold of the goods its wars actually spend: iron for
 * musters (`RAISE_IRON_PER_POINT` × a ~660-point host is 40), timber and stone
 * for hulls and harbours. Each target is strictly below its `AI_KEEP` — see
 * there, and see there too for why grain is in neither table.
 */
export const AI_TARGET: Partial<Record<ResourceKey, number>> = {
  iron: 40,
  timber: 30,
  stone: 15,
};

/** Smallest lot worth a line on the book, and the biggest posted at once. */
export const AI_MIN_LOT = 10;
export const AI_MAX_LOT = 80;

/**
 * Gold a crown keeps back before bidding. Set against the muster: a host
 * costs ~200 gold and the market must never be the reason a crown cannot
 * defend itself.
 */
export const AI_GOLD_CUSHION = 140;

/**
 * Ask price: `base × (1 + SELL_SCARCITY / (1 + rival sellers))`.
 *
 * A lone seller asks 1.35× base; three rivals pull the ask to ~1.09×; it never
 * goes below base — a crown does not sell at a loss to its own anchor. This is
 * the "scaled up when few sellers" of the brief, priced off the book itself.
 */
export const SELL_SCARCITY = 0.35;

/**
 * Bid price: `base × (BUY_BASE + BUY_DESPERATION × desperation)`, desperation
 * being how far below target the stock has fallen (0..1).
 *
 * A calm crown bids 0.65× base and waits; a crown at zero stock bids 1.10× —
 * above base, and above a well-competed ask, which is exactly when the market
 * should move. The trade still executes at the seller's price: the desperate
 * buyer takes the cheapest ask on the book, not its own limit.
 */
export const BUY_BASE = 0.65;
export const BUY_DESPERATION = 0.45;
